import { yupResolver } from "@hookform/resolvers/yup";
import FormHubSelect from "components/Common/Form/FormHubSelect";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { useListTransport, useUpdateCustomerLocationTransport } from "hooks";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Col,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { Switch } from "antd";
import * as yup from "yup";
import useCustomHook from "../useCustomHook";

const UpdateLocationTransportModal = ({
  choosenData,
  customerId,
  isOpenUpdateModal,
  toggleUpdateModal,
  fetchCustomerLocationTransports,
}) => {
  const { t } = useTranslation(["customer"]);
  const [is_level_2, setIsLevel2] = useState(true);
  const [canSwitch, setCanSwitch] = useState(false);

  const getValidationSchema = () => {
    return yup.object().shape({
      transport_id: yup
        .object()
        .required(
          t("form.validate.select", {
            title: t("customer_location_transport_table.transport"),
          })
        )
        .nullable(),
      province_code: yup
        .object()
        .required(
          t("form.validate.select", {
            title: t("customer_location_transport_table.province"),
          })
        )
        .nullable(),
      district_code: yup
        .object()
        .required(
          t("form.validate.select", {
            title: is_level_2
              ? "Phường xã"
              : t("customer_location_transport_table.district"),
          })
        )
        .nullable(),
      ward_code: yup.object().when([], {
        is: () => is_level_2,
        then: yup.object().nullable(),
        otherwise: yup
          .object()
          .nullable()
          .required(
            t("form.validate.select", {
              title: t("customer_location_transport_table.ward"),
            })
          ),
      }),
      hub_id: yup
        .object()
        .required(
          t("form.validate.select", {
            title: t("customer_location_transport_table.hub"),
          })
        )
        .nullable(),
    });
  };

  const updateForm = useForm({
    resolver: yupResolver(getValidationSchema()),
    mode: "all",
    reValidateMode: "onChange",
  });

  const {
    formState: { errors },
    reset,
    getValues,
    control,
    trigger,
    setValue,
    watch,
  } = updateForm;

  const { provinceOptions, districtOptions, wardOptions } = useCustomHook({
    mainForm: updateForm,
    isClearDistrictAfterChooseProvince: false,
    isClearWardAfterChooseDistrict: false,
    is_level_2,
  });

  const hubWatch = watch("hub_id");
  const provinceWatch = watch("province_code");
  const districtWatch = watch("district_code");

  const onSaveUpdate = () => {
    trigger().then(result => {
      if (result) {
        const data = getValues();
        const payload = {
          id: choosenData?.id,
          data: {
            ward_code: is_level_2 ? null : data.ward_code?.value,
            district_code: data.district_code.value,
            province_code: data.province_code.value,
            transport_id: data.transport_id.value,
            hub_id: data.hub_id.value,
            customer_id: customerId,
          },
        };

        updateCustomerLocationTransport(payload);
      }
    });
  };

  const onSuccesUpdate = () => {
    toggleUpdateModal("close");
    fetchCustomerLocationTransports();
  };

  const { mutate: updateCustomerLocationTransport } =
    useUpdateCustomerLocationTransport(onSuccesUpdate);

  const handleChangeIsLevel2 = newIsLevel2 => {
    setIsLevel2(newIsLevel2);
    setValue("province_code", null);
    setValue("district_code", null);
    setValue("ward_code", null);
    setValue("hub_id", null);
    setValue("transport_id", null);
  };
  useEffect(() => {
    if (choosenData && Object.keys(choosenData).length > 0) {
      const hasWard = !!choosenData.ward?.code;

      if (hasWard) {
        setIsLevel2(false);
        setCanSwitch(true);
      } else {
        setIsLevel2(true);
        setCanSwitch(false);
      }

      reset({
        transport_id: choosenData.transport
          ? {
              label: choosenData.transport.name,
              value: choosenData.transport.id,
            }
          : null,
        province_code: choosenData.province
          ? {
              label: choosenData.province.name,
              value: choosenData.province.code,
            }
          : null,
        district_code: choosenData.district
          ? {
              label: choosenData.district.name,
              value: choosenData.district.code,
            }
          : null,
        ward_code: hasWard
          ? { label: choosenData.ward.name, value: choosenData.ward.code }
          : null,
        hub_id: choosenData.hub
          ? { label: choosenData.hub.name, value: choosenData.hub.id }
          : null,
      });

      setTransportParams(prev => ({
        ...prev,
        columnFilter: {
          ...prev.columnFilter,
          "filter[hub_id]": choosenData.hub?.id,
        },
      }));
    }
  }, [choosenData, reset]);

  const onChangeProvince = province => {
    setValue("province_code", province);
    if (province.value !== choosenData?.province?.code) {
      setValue("district_code", null);
      setValue("ward_code", null);
      setValue("hub_id", null);
      setValue("transport_id", null);
    } else {
      setValue("district_code", {
        label: choosenData.district?.name,
        value: choosenData.district?.code,
      });
      if (!is_level_2) {
        setValue("ward_code", {
          label: choosenData.ward?.name,
          value: choosenData.ward?.code,
        });
      }
      setValue("hub_id", {
        label: choosenData.hub?.name,
        value: choosenData.hub?.id,
      });
      setValue("transport_id", {
        label: choosenData.transport?.name,
        value: choosenData.transport?.id,
      });
    }
  };

  const onChangeDistrict = district => {
    setValue("district_code", district);
    if (district.value !== choosenData?.district?.code) {
      setValue("ward_code", null);
      setValue("hub_id", null);
      setValue("transport_id", null);
    } else if (!is_level_2) {
      setValue("ward_code", {
        label: choosenData.ward?.name,
        value: choosenData.ward?.code,
      });
    }
    setValue("hub_id", null);
    setValue("transport_id", null);
  };

  const onChangeHub = hub => {
    setValue("hub_id", hub);
    if (hub.value !== choosenData?.hub?.id) {
      setValue("transport_id", null);
    } else {
      setValue("transport_id", {
        label: choosenData.transport?.name,
        value: choosenData.transport?.id,
      });
    }
    setTransportParams(prev => ({
      ...prev,
      columnFilter: {
        ...prev.columnFilter,
        "filter[hub_id]": hub.value,
      },
    }));
  };

  const cancelUpdate = () => {
    reset({});
    setIsLevel2(true);
    toggleUpdateModal("close");
  };

  const [transportParams, setTransportParams] = useState({
    sizePerPage: 100,
    columnFilter: {
      "filter[record_status]": "O",
    },
    fields: "id,name",
  });

  const { data: transportList, refetch: fetchTransportList } = useListTransport(
    {
      params: transportParams,
    }
  );

  const optionsTransportSelect = useMemo(() => {
    return transportList?.items?.map(item => ({
      label: item.name,
      value: item.id,
    }));
  }, [transportList]);

  useEffect(() => {
    fetchTransportList();
  }, [transportParams]);

  return (
    <Modal
      size="md"
      isOpen={isOpenUpdateModal}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      toggle={cancelUpdate}
    >
      <ModalHeader toggle={cancelUpdate}>
        {t("customer_location_transport_table.title.edit", {
          target: t("customer_location_transport_table.transport"),
        })}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col xs={12}>
            {canSwitch && (
              <FormGroup>
                <Label
                  className="d-flex align-items-center gap-1"
                  style={{ cursor: "pointer" }}
                  htmlFor="is_level_2"
                  onClick={() => handleChangeIsLevel2(!is_level_2)}
                >
                  <Switch
                    checked={is_level_2}
                    onChange={handleChangeIsLevel2}
                  />
                  Địa chỉ 2 cấp
                </Label>
              </FormGroup>
            )}
          </Col>
          <Col xs={6}>
            <FormGroup>
              <Label htmlFor="province_code">
                {t("customer_location_transport_table.province")}{" "}
                <span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="province_code"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder.select", {
                      title: t("customer_location_transport_table.province"),
                    })}
                    onChange={onChangeProvince}
                    options={provinceOptions}
                    isSearchable
                    className="basic-single w-100"
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.province_code && (
                <MessageFormValidate error={errors.province_code.message} />
              )}
            </FormGroup>
          </Col>

          <Col xs={6}>
            <FormGroup>
              <Label htmlFor="district_code">
                {is_level_2
                  ? "Phường xã"
                  : t("customer_location_transport_table.district")}{" "}
                <span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="district_code"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder.select", {
                      title: is_level_2
                        ? "Phường xã"
                        : t("customer_location_transport_table.district"),
                    })}
                    options={districtOptions}
                    onChange={onChangeDistrict}
                    isSearchable
                    className="basic-single w-100"
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.district_code && (
                <MessageFormValidate error={errors.district_code.message} />
              )}
            </FormGroup>
          </Col>

          {!is_level_2 && (
            <Col xs={6}>
              <FormGroup>
                <Label htmlFor="ward_code">
                  {t("customer_location_transport_table.ward")}{" "}
                  <span className="text-danger">*</span>
                </Label>
                <Controller
                  control={control}
                  name="ward_code"
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder={t("placeholder.select", {
                        title: t("customer_location_transport_table.ward"),
                      })}
                      options={wardOptions}
                      isSearchable
                      className="basic-single w-100"
                      classNamePrefix="select"
                    />
                  )}
                />
                {errors.ward_code && (
                  <MessageFormValidate error={errors.ward_code.message} />
                )}
              </FormGroup>
            </Col>
          )}

          <Col xs={6}>
            <FormHubSelect
              isMyHubOnly
              isRequired
              mainForm={updateForm}
              fieldName="hub_id"
              t={t}
              title={t("customer_location_transport_table.hub")}
              onChange={onChangeHub}
              addressLevel={is_level_2 ? 2 : 3}
              provinceCode={provinceWatch?.value}
              districtCode={districtWatch?.value}
              key={`hub-select-${is_level_2}`}
            />
          </Col>

          <Col xs={6}>
            <FormGroup>
              <Label htmlFor="transport_id">
                {t("customer_location_transport_table.transport")}{" "}
                <span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="transport_id"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder.select", {
                      title: t("customer_location_transport_table.transport"),
                    })}
                    options={optionsTransportSelect || []}
                    isSearchable
                    className="basic-single w-100"
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.transport_id && (
                <MessageFormValidate error={errors.transport_id.message} />
              )}
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          size="xs"
          onClick={cancelUpdate}
          className="btn btn-danger btn-label"
        >
          <i className="fas fa-ban label-icon"></i>
          {t("customer_location_transport_table.actions.cancel")}
        </Button>
        <Button
          size="xs"
          style={{ backgroundColor: "#3C64B1" }}
          onClick={onSaveUpdate}
          className="btn btn-primary btn-label"
        >
          <i className="fas fa-save label-icon"></i>
          {t("customer_location_transport_table.actions.save")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UpdateLocationTransportModal;
